import {
  InfoCircleOutlined,
  LinkOutlined,
  UserOutlined,
  DashboardOutlined,
  PercentageOutlined,
  PoweroffOutlined,
} from '@ant-design/icons';
import { StageTaskType } from '@/store/Lifecycle/constants';
import { TaskSectionProps, TaskFieldMetadata } from './types';
import {
  CalendarBlankIcon,
  CheckedListIcon,
  CalendarCheckIcon,
  HardHatIcon,
  MapIcon,
  GitCommitIcon,
  NotepadIcon,
  PackageIcon,
  ShieldCheckIcon,
  CoinsIcon,
} from './icons';
import { LifecycleStoreTask } from '@/store/Lifecycle/types';
import {
  BooleanTask,
  DateRangeTask,
  DateTask,
  EnumTask,
  LinkTask,
  TextTask,
  UserIdTask,
  UnitsTask,
  PercentageTask,
  MeasurementUnitTask,
} from '../constants';
import { toCurrencySymbol } from '@/utils/currency.utils';

type Task =
  | BooleanTask
  | DateRangeTask
  | DateTask
  | EnumTask
  | LinkTask
  | TextTask
  | UserIdTask
  | UnitsTask
  | PercentageTask
  | MeasurementUnitTask;

export enum GroupedTask {}
// TODO: add grouped tasks here

export const groupedTasksMapper: { [task in Task]?: GroupedTask } = {
  // TODO: add grouped tasks here
};

export const getTaskSectionMetadata = (
  name: string /*, tasks?: LifecycleStoreTask<StageTaskType>[]*/,
): TaskSectionProps => {
  const metadata: { [task in Task | GroupedTask]?: TaskSectionProps } = {
    [BooleanTask.APPROVE_PID]: {
      title: BooleanTask.APPROVE_PID,
      icon: <InfoCircleOutlined />,
    },
    [BooleanTask.STAGING_READY]: {
      title: BooleanTask.STAGING_READY,
      icon: <MapIcon />,
    },
    [BooleanTask.AIR_EXPERT_APPROVAL]: {
      title: BooleanTask.AIR_EXPERT_APPROVAL,
      icon: <InfoCircleOutlined />,
    },
    [DateRangeTask.BASELINE_PERIOD]: {
      title: DateRangeTask.BASELINE_PERIOD,
      icon: <CalendarCheckIcon />,
    },
    [DateRangeTask.INSTALLATION_DATES]: {
      title: DateRangeTask.INSTALLATION_DATES,
      icon: <CalendarCheckIcon />,
    },
    [DateTask.KICKOFF_MEETING_DATE]: {
      title: DateTask.KICKOFF_MEETING_DATE,
      icon: <CalendarCheckIcon />,
    },
    [DateTask.PARTS_ARRIVED_DATE]: {
      title: DateTask.PARTS_ARRIVED_DATE,
      icon: <CalendarCheckIcon />,
    },
    [DateTask.PLANNED_INSTALLATION_DATE]: {
      title: DateTask.PLANNED_INSTALLATION_DATE,
      icon: <CalendarBlankIcon />,
    },
    [DateTask.INSTALLATION_VALIDATION_DATE]: {
      title: DateTask.INSTALLATION_VALIDATION_DATE,
      icon: <CheckedListIcon />,
    },
    [DateTask.TRAINING_MEETING_DATE]: {
      title: DateTask.TRAINING_MEETING_DATE,
      icon: <CalendarBlankIcon />,
    },
    [DateTask.ENGINE_ACTIVATION_DATE]: {
      title: DateTask.ENGINE_ACTIVATION_DATE,
      icon: <CalendarBlankIcon />,
    },
    [DateTask.PO_START_DATE]: {
      title: DateTask.PO_START_DATE,
      icon: <CalendarBlankIcon />,
    },
    [DateTask.ENGINE_CREATION_DATE]: {
      title: DateTask.ENGINE_CREATION_DATE,
      icon: <CalendarBlankIcon />,
    },
    [EnumTask.OPTIMIZE_NOT_CONTROLLED_STATION]: {
      title: EnumTask.OPTIMIZE_NOT_CONTROLLED_STATION,
      subtitle: `${EnumTask.OPTIMIZE_NOT_CONTROLLED_STATION}-subtitle`,
      icon: <GitCommitIcon />,
    },
    [LinkTask.MEETING_PRESENTATION]: {
      title: LinkTask.MEETING_PRESENTATION,
      subtitle: 'Add a link to document',
      icon: <LinkOutlined />,
    },
    [LinkTask.STATION_AS_BUILT]: {
      title: LinkTask.STATION_AS_BUILT,
      icon: <MapIcon />,
      subtitle: 'Add a link to document',
    },
    [LinkTask.LINK_BOM_DOCUMENT]: {
      title: LinkTask.LINK_BOM_DOCUMENT,
      icon: <CheckedListIcon />,
      subtitle: 'Add a link to document',
    },
    [LinkTask.BASELINE_REPORT]: {
      title: LinkTask.BASELINE_REPORT,
      icon: <NotepadIcon />,
      subtitle: 'Add a link to document',
    },
    [LinkTask.TRAINING_SESSION_SUMMARY]: {
      title: LinkTask.TRAINING_SESSION_SUMMARY,
      icon: <CheckedListIcon />,
      subtitle: 'Add a link to document',
    },
    [LinkTask.INSTALLATION_APPROVAL_REPORT]: {
      title: LinkTask.INSTALLATION_APPROVAL_REPORT,
      icon: <HardHatIcon />,
      subtitle: 'Add a link to document',
    },
    [LinkTask.COMPRESSOR_CONTROL_TEST_EXECUTED]: {
      title: LinkTask.COMPRESSOR_CONTROL_TEST_EXECUTED,
      icon: <CheckedListIcon />,
      subtitle: 'Add link to SCC activity log screenshot',
    },
    [TextTask.ORDER_PURCHASE_NUMBER]: {
      title: TextTask.ORDER_PURCHASE_NUMBER,
      icon: <ShieldCheckIcon />,
    },
    [TextTask.PARTS_TRACKING]: {
      title: TextTask.PARTS_TRACKING,
      icon: <PackageIcon />,
      subtitle: 'More than one tracking number can be added.',
    },
    [TextTask.CONTRACT_NUMBER]: {
      title: TextTask.CONTRACT_NUMBER,
      icon: <LinkOutlined />,
    },
    [TextTask.PARTS_ORDER_NUMBER]: {
      title: TextTask.PARTS_ORDER_NUMBER,
      icon: <PackageIcon />,
      subtitle: 'More than one order number can be added.',
    },
    [UserIdTask.ASSIGN_CS_OWNER]: {
      title: UserIdTask.ASSIGN_CS_OWNER,
      icon: <UserOutlined />,
    },
    [UserIdTask.ASSIGN_ENGINE_OWNER]: {
      title: UserIdTask.ASSIGN_ENGINE_OWNER,
      icon: <UserOutlined />,
    },
    [UnitsTask.KW_COST]: {
      title: UnitsTask.KW_COST,
      icon: <CoinsIcon />,
    },
    [BooleanTask.KPI_RANGES_VALIDATION]: {
      title: BooleanTask.KPI_RANGES_VALIDATION,
      icon: <InfoCircleOutlined />,
    },
    [BooleanTask.INSTALLATION_IMAGES_TAKEN]: {
      title: BooleanTask.INSTALLATION_IMAGES_TAKEN,
      icon: <InfoCircleOutlined />,
    },
    [PercentageTask.SAVINGS_COMMITMENT]: {
      title: PercentageTask.SAVINGS_COMMITMENT,
      icon: <PercentageOutlined />,
    },
    [MeasurementUnitTask.AVERAGE_SPECIFIC_POWER]: {
      title: MeasurementUnitTask.AVERAGE_SPECIFIC_POWER,
      icon: <PoweroffOutlined />,
    },
    [TextTask.ENGINE_VERSION]: {
      title: TextTask.ENGINE_VERSION,
      icon: <DashboardOutlined />,
      tooltip: 'Engine version is set by engine expert',
    },
  };
  return metadata[name];
};

export const getTaskFieldMetadata = <T extends StageTaskType>(
  task: LifecycleStoreTask<T>,
): TaskFieldMetadata & LifecycleStoreTask<T> => {
  const metadata: {
    [task in
      | BooleanTask
      | DateRangeTask
      | DateTask
      | EnumTask
      | LinkTask
      | TextTask
      | UserIdTask
      | UnitsTask
      | PercentageTask
      | MeasurementUnitTask]?: TaskFieldMetadata;
  } = {
    [DateTask.KICKOFF_MEETING_DATE]: {
      placeholder: 'Select',
    },
    [LinkTask.MEETING_PRESENTATION]: {
      placeholder: 'Add a link to presentation',
    },
    [LinkTask.STATION_AS_BUILT]: {
      placeholder: 'Add a link to presentation',
    },
    [LinkTask.LINK_BOM_DOCUMENT]: {
      placeholder: 'Add a link to document',
    },
    [LinkTask.BASELINE_REPORT]: {
      placeholder: 'Add a link to report',
    },
    [LinkTask.TRAINING_SESSION_SUMMARY]: {
      placeholder: 'Add a link to document',
    },
    [LinkTask.INSTALLATION_APPROVAL_REPORT]: {
      placeholder: 'Add a link to document',
    },
    [LinkTask.COMPRESSOR_CONTROL_TEST_EXECUTED]: {
      placeholder: 'Add link to SCC activity log screenshot',
    },
    [UnitsTask.KW_COST]: {
      optionMapper: ({ value }) => {
        return {
          label: `${value} (${toCurrencySymbol(value)})`,
          value,
        };
      },
    },
  };
  return { ...task, ...metadata[task.name] };
};

export const groupTasks = (
  tasks: LifecycleStoreTask<StageTaskType>[],
): { name: string; tasks: LifecycleStoreTask<StageTaskType>[] }[] => {
  const groupedTasks = tasks.reduce((tasksByGroup, task) => {
    if (task.name in groupedTasksMapper) {
      tasksByGroup[groupedTasksMapper[task.name]] = {
        tasks: [...(tasksByGroup[groupedTasksMapper[task.name]]?.tasks || []), task],
        order: task.order,
      };
    } else {
      tasksByGroup[task.name] = { tasks: [task], order: task.order };
    }
    return tasksByGroup;
  }, {} as Record<string, { tasks: LifecycleStoreTask<StageTaskType>[]; order: number }>);
  return Object.entries(groupedTasks)
    .sort((a, b) => a[1].order - b[1].order)
    .map(([name, { tasks }]) => ({ name, tasks }));
};
